/**
 * useJobEvents Hook
 *
 * WebSocket hook for subscribing to job queue events.
 * Manages jobs and logs state from the BRUTAL agent.
 */

import { useState, useEffect, useCallback } from 'react';
import { z } from 'zod';
import type { LogEntry } from '@brutal/brutal/organisms';
import { getRpcClient } from '../lib/rpc-client';

// ═══════════════════════════════════════════════════════════════
// JOB SCHEMAS (browser-safe copy from @brutal/job-queue)
// ═══════════════════════════════════════════════════════════════

/** Job type enumeration */
const JobTypeSchema = z.enum(['pipeline', 'deploy', 'command']);

/** Job status enumeration */
const JobStatusSchema = z.enum(['pending', 'running', 'completed', 'failed', 'cancelled']);

/** Job priority enumeration */
const JobPrioritySchema = z.enum(['low', 'normal', 'high', 'critical']);

/** Pipeline job payload */
const PipelineJobPayloadSchema = z.object({
  type: z.literal('pipeline'),
  pipelineId: z.string().min(1),
  ref: z.string().min(1),
  commitSha: z.string().min(1),
  variables: z.record(z.string(), z.string()).nullable(),
  triggeredBy: z.string().nullable(),
});

/** Deploy job payload */
const DeployJobPayloadSchema = z.object({
  type: z.literal('deploy'),
  environment: z.string().min(1),
  artifactId: z.string().min(1),
  version: z.string().min(1),
  rollbackVersion: z.string().nullable(),
  dryRun: z.boolean(),
});

/** Command job payload */
const CommandJobPayloadSchema = z.object({
  type: z.literal('command'),
  command: z.string().min(1),
  args: z.array(z.string()),
  workDir: z.string().nullable(),
  env: z.record(z.string(), z.string()).nullable(),
  timeoutMs: z.number().positive().nullable(),
});

/** Discriminated union of all job payloads */
const JobPayloadSchema = z.discriminatedUnion('type', [
  PipelineJobPayloadSchema,
  DeployJobPayloadSchema,
  CommandJobPayloadSchema,
]);

/** Full job record with all metadata */
const JobRecordSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  payload: JobPayloadSchema,
  status: JobStatusSchema,
  priority: JobPrioritySchema,
  progress: z.number().min(0).max(100),
  attemptsMade: z.number().nonnegative(),
  maxAttempts: z.number().positive(),
  result: z.unknown().nullable(),
  error: z.string().nullable(),
  stacktrace: z.array(z.string()).nullable(),
  createdAt: z.string().datetime(),
  processedAt: z.string().datetime().nullable(),
  completedAt: z.string().datetime().nullable(),
  failedAt: z.string().datetime().nullable(),
});

/**
 * JobRecord — result is required+nullable to match the organisms component contract.
 * z.unknown() makes `result` optional in Zod's inferred type (undefined ⊂ unknown),
 * so we override it here.
 */
export type JobRecord = Omit<z.infer<typeof JobRecordSchema>, 'result'> & { result: unknown | null };

/** Normalize a raw Zod-parsed job into a JobRecord (handles z.unknown() optional quirk). */
function normalizeJobRecord(raw: z.infer<typeof JobRecordSchema>): JobRecord {
  return {
    id: raw.id,
    name: raw.name,
    payload: raw.payload,
    status: raw.status,
    priority: raw.priority,
    progress: raw.progress,
    attemptsMade: raw.attemptsMade,
    maxAttempts: raw.maxAttempts,
    result: raw.result ?? null,
    error: raw.error,
    stacktrace: raw.stacktrace,
    createdAt: raw.createdAt,
    processedAt: raw.processedAt,
    completedAt: raw.completedAt,
    failedAt: raw.failedAt,
  };
}

// ═══════════════════════════════════════════════════════════════
// JOB EVENT SCHEMAS
// ═══════════════════════════════════════════════════════════════

/** Job created event */
const JobCreatedEventSchema = z.object({
  jobId: z.string().min(1),
  name: z.string().min(1),
  jobType: JobTypeSchema,
  priority: JobPrioritySchema,
  createdAt: z.string().datetime(),
});

/** Job started event */
const JobStartedEventSchema = z.object({
  jobId: z.string().min(1),
  name: z.string().min(1),
  attemptNumber: z.number().positive(),
  startedAt: z.string().datetime(),
});

/** Job completed event */
const JobCompletedEventSchema = z.object({
  jobId: z.string().min(1),
  name: z.string().min(1),
  result: z.unknown().nullable(),
  durationMs: z.number().nonnegative(),
  completedAt: z.string().datetime(),
});

/** Job failed event */
const JobFailedEventSchema = z.object({
  jobId: z.string().min(1),
  name: z.string().min(1),
  error: z.string(),
  attemptNumber: z.number().positive(),
  willRetry: z.boolean(),
  failedAt: z.string().datetime(),
});

/** Job cancelled event */
const JobCancelledEventSchema = z.object({
  jobId: z.string().min(1),
  name: z.string().min(1),
  reason: z.string().nullable(),
  cancelledAt: z.string().datetime(),
});

/** Job progress event */
const JobProgressEventSchema = z.object({
  jobId: z.string().min(1),
  name: z.string().min(1),
  progress: z.number().min(0).max(100),
  message: z.string().nullable(),
});

// ═══════════════════════════════════════════════════════════════
// SCHEMAS
// ═══════════════════════════════════════════════════════════════

/** Job list response schema */
const JobListResponseSchema = z.object({
  jobs: z.array(JobRecordSchema),
  total: z.number(),
});

// ═══════════════════════════════════════════════════════════════
// HOOK
// ═══════════════════════════════════════════════════════════════

export interface UseJobEventsOptions {
  /** Maximum number of logs to keep in memory */
  maxLogs: number | null;
  /** Whether to auto-fetch jobs on connect */
  autoFetch: boolean;
}

export interface UseJobEventsResult {
  jobs: JobRecord[];
  logs: LogEntry[];
  isLoading: boolean;
  error: string | null;
  refreshJobs: () => Promise<void>;
  clearLogs: () => void;
}

/**
 * Hook for subscribing to job events via WebSocket.
 * Maintains local state of jobs and streaming logs.
 */
export function useJobEvents(options: UseJobEventsOptions | null = null): UseJobEventsResult {
  const maxLogs = options?.maxLogs ?? 500;
  const autoFetch = options?.autoFetch ?? true;

  const [jobs, setJobs] = useState<JobRecord[]>([]);
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // ─────────────────────────────────────────────────────────────────
  // LOG HELPER
  // ─────────────────────────────────────────────────────────────────

  const addLog = useCallback(
    (
      level: LogEntry['level'],
      message: string,
      metadata: Record<string, unknown> | null = null,
    ) => {
      setLogs((prev) => {
        const newLog: LogEntry = {
          timestamp: new Date().toISOString(),
          level,
          message,
          metadata,
        };
        const updated = [...prev, newLog];
        // Trim to maxLogs
        if (updated.length > maxLogs) {
          return updated.slice(-maxLogs);
        }
        return updated;
      });
    },
    [maxLogs],
  );

  // ─────────────────────────────────────────────────────────────────
  // JOB UPDATE HELPERS
  // ─────────────────────────────────────────────────────────────────

  const updateJob = useCallback((jobId: string, updates: Partial<JobRecord>) => {
    setJobs((prev) => prev.map((job) => (job.id === jobId ? { ...job, ...updates } : job)));
  }, []);

  const addJob = useCallback((job: JobRecord) => {
    setJobs((prev) => {
      // Check if job already exists
      const exists = prev.some((j) => j.id === job.id);
      if (exists) {
        return prev.map((j) => (j.id === job.id ? job : j));
      }
      return [job, ...prev];
    });
  }, []);

  // ─────────────────────────────────────────────────────────────────
  // REFRESH JOBS
  // ─────────────────────────────────────────────────────────────────

  const refreshJobs = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    setError(null);
    try {
      const client = getRpcClient();
      const response = await client.call(
        'job.list',
        { status: null, limit: 100, offset: null },
        JobListResponseSchema,
      );
      setJobs(response.jobs.map(normalizeJobRecord));
      addLog('info', `Loaded ${response.jobs.length} jobs`);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch jobs';
      setError(message);
      addLog('error', `Failed to load jobs: ${message}`);
    } finally {
      setIsLoading(false);
    }
  }, [addLog]);

  // ─────────────────────────────────────────────────────────────────
  // CLEAR LOGS
  // ─────────────────────────────────────────────────────────────────

  const clearLogs = useCallback(() => {
    setLogs([]);
  }, []);

  // ─────────────────────────────────────────────────────────────────
  // SUBSCRIBE TO EVENTS
  // ─────────────────────────────────────────────────────────────────

  useEffect(() => {
    const client = getRpcClient();
    const unsubscribers: Array<() => void> = [];
    let isMounted = true;

    // Job created
    unsubscribers.push(
      client.subscribe('job.created', (data) => {
        try {
          const event = JobCreatedEventSchema.parse(data);
          addLog('info', `Job created: ${event.name}`, {
            jobId: event.jobId,
            priority: event.priority,
          });
          // Fetch full job record
          client
            .call('job.get', { jobId: event.jobId }, JobRecordSchema)
            .then((job) => addJob(normalizeJobRecord(job)))
            .catch(() => {
              // Ignore if job fetch fails
            });
        } catch {
          // Ignore invalid event
        }
      }),
    );

    // Job started
    unsubscribers.push(
      client.subscribe('job.started', (data) => {
        try {
          const event = JobStartedEventSchema.parse(data);
          addLog('info', `Job started: ${event.name}`, {
            jobId: event.jobId,
            attempt: event.attemptNumber,
          });
          updateJob(event.jobId, {
            status: 'running',
            processedAt: event.startedAt,
            attemptsMade: event.attemptNumber,
          });
        } catch {
          // Ignore invalid event
        }
      }),
    );

    // Job completed
    unsubscribers.push(
      client.subscribe('job.completed', (data) => {
        try {
          const event = JobCompletedEventSchema.parse(data);
          addLog('info', `Job completed: ${event.name}`, {
            jobId: event.jobId,
            durationMs: event.durationMs,
          });
          updateJob(event.jobId, {
            status: 'completed',
            completedAt: event.completedAt,
            result: event.result,
          });
        } catch {
          // Ignore invalid event
        }
      }),
    );

    // Job failed
    unsubscribers.push(
      client.subscribe('job.failed', (data) => {
        try {
          const event = JobFailedEventSchema.parse(data);
          const level = event.willRetry ? 'warn' : 'error';
          const message = event.willRetry
            ? `Job failed (will retry): ${event.name}`
            : `Job failed: ${event.name}`;
          addLog(level, message, {
            jobId: event.jobId,
            error: event.error,
            attempt: event.attemptNumber,
          });
          updateJob(event.jobId, {
            status: event.willRetry ? 'running' : 'failed',
            error: event.error,
            failedAt: event.failedAt,
            attemptsMade: event.attemptNumber,
          });
        } catch {
          // Ignore invalid event
        }
      }),
    );

    // Job cancelled
    unsubscribers.push(
      client.subscribe('job.cancelled', (data) => {
        try {
          const event = JobCancelledEventSchema.parse(data);
          addLog('warn', `Job cancelled: ${event.name}`, {
            jobId: event.jobId,
            reason: event.reason,
          });
          updateJob(event.jobId, {
            status: 'cancelled',
          });
        } catch {
          // Ignore invalid event
        }
      }),
    );

    // Job progress
    unsubscribers.push(
      client.subscribe('job.progress', (data) => {
        try {
          const event = JobProgressEventSchema.parse(data);
          if (event.message) {
            addLog('debug', `Job progress: ${event.name} - ${event.progress}%`, {
              jobId: event.jobId,
              message: event.message,
            });
          }
          updateJob(event.jobId, {
            progress: event.progress,
          });
        } catch {
          // Ignore invalid event
        }
      }),
    );

    // Auto-fetch jobs on mount - wait for connection first
    if (autoFetch) {
      client
        .waitForReady(10000)
        .then(() => {
          if (isMounted) {
            refreshJobs();
          }
        })
        .catch((err) => {
          if (isMounted) {
            const message = err instanceof Error ? err.message : 'Connection failed';
            setError(message);
            setIsLoading(false);
          }
        });
    } else {
      setIsLoading(false);
    }

    // Cleanup
    return () => {
      isMounted = false;
      unsubscribers.forEach((unsub) => unsub());
    };
  }, [autoFetch, addLog, addJob, updateJob, refreshJobs]);

  return {
    jobs,
    logs,
    isLoading,
    error,
    refreshJobs,
    clearLogs,
  };
}
